import { loadAddressValidationLib } from "helpers/googleMaps";

export type AddressUnitCheckInput = {
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
};

export type AddressUnitCheckResult = "unit_missing" | "ok";

/**
 * Canonical cache/identity key for an address. Callers that need to detect
 * "the address changed" must use this same normalization so their notion of
 * identity can never drift from the cache's.
 */
export function addressKey(input: AddressUnitCheckInput): string {
  return [input.addressLine1, input.city, input.state, input.zip]
    .map((part) => part.trim().toLowerCase())
    .join("|");
}

const resultCache = new Map<string, AddressUnitCheckResult>();

/**
 * Returns "unit_missing" when Google Address Validation reports the US address
 * likely needs an apartment/unit number (missing subpremise). Fails open: any
 * load/API error resolves to "ok" so customers are never blocked by an outage.
 */
export async function checkAddressNeedsUnit(
  input: AddressUnitCheckInput,
): Promise<AddressUnitCheckResult> {
  const key = addressKey(input);

  const cached = resultCache.get(key);
  if (cached) return cached;

  try {
    const { AddressValidation } = await loadAddressValidationLib();
    const result = await AddressValidation.fetchAddressValidation({
      address: {
        regionCode: "US",
        addressLines: [input.addressLine1],
        locality: input.city,
        administrativeArea: input.state,
        postalCode: input.zip,
      },
    });

    const missingSubpremise = (
      result.address?.missingComponentTypes ?? []
    ).includes("subpremise");
    const nextAction = String(result.verdict?.possibleNextAction ?? "");
    const needsUnit =
      missingSubpremise ||
      nextAction.toUpperCase() === "CONFIRM_ADD_SUBPREMISES";

    const outcome: AddressUnitCheckResult = needsUnit ? "unit_missing" : "ok";
    resultCache.set(key, outcome);
    return outcome;
  } catch (error) {
    console.error("Error validating address", error);
    return "ok";
  }
}
